'use strict';
import moment from 'moment';
import {Injectable} from '@angular/core';
import {Observable, of, zip} from 'rxjs';
import {
  cloneDeep,
  each as lodash_each,
  filter as lodashFilter,
  find,
  flatten,
  get,
  isEmpty,
  isEqual,
  keyBy,
  mapValues,
  uniqWith
} from 'lodash';
import {map, switchMap, take} from 'rxjs/operators';

import {AdaptiveFilterService} from '@ispm/shared/services/adaptive-filter.service';
import {AgregationService} from '@ispm-client/modules/aggregator/services/aggregator.service';
import {
  BundleDynamicSearchPayload,
  DynamicSearchPayload,
  Sort,
  Target
} from '@ispm-shared/modules/common/dynamic-search';
import {ConfigurationService} from '../../configuration/services/configuration.service';
import {Filter} from '@ispm-shared/modules/common';
import {FilterService} from '../../filter/services/filter.service';
import {IRevision} from '@ispm/workflow/interfaces/revision.interface';
import {IspmHttpClient} from '@ispm/core/services/ispm-http-client';
import {ReviseService} from '@ispm/workflow/services/revise.service';
import {ActivatedRoute} from "@angular/router";
import {WorklistHelper} from '../worklist-helper';
import {HttpInterceptorService} from '@ispm/core/services/http-interceptor.service';
import {NavigationConfigService} from '@ispm/navigation/services/navigationConfig.service';
import {ResourceHelperService} from './resource-helper.services';
import {Store} from '@ngrx/store';
import {RouterManagerService} from "@ispm/core/services/router-manager.service";

@Injectable()
export class DynamicSearchService  extends ResourceHelperService {
  private _dashboardFilters: Array<Filter> = [];

  constructor(
    protected _http: HttpInterceptorService,
    protected _navigationConfigService: NavigationConfigService,
    private _adaptiveFilterService: AdaptiveFilterService,
    private _aggregationService: AgregationService,
    private _configurationService: ConfigurationService,
    private _filterService: FilterService,
    private _ispmHttpClient: IspmHttpClient,
    private _store: Store<any>,
    private routerManagerService: RouterManagerService,
  ) {
    super(_http, _navigationConfigService);
  }

  public sendAggregationDataQuery(resourceType: string, query: DynamicSearchPayload): Observable<any> {
    return this._ispmHttpClient.post('entity-tree/' + resourceType + '/dynamicSearch?type=data&aggregate=true', query);
  }

  public getTotalEntities() {
    let dynamicSearchQuery: DynamicSearchPayload = cloneDeep(this._dynamicSearch);
    dynamicSearchQuery.target._count = 0;
    dynamicSearchQuery.target._page  = 0;
    dynamicSearchQuery.sort  = [];
    dynamicSearchQuery = this.setAdditionalFilters(dynamicSearchQuery);

    return this.applyFilterProperties().pipe(
      take(1),
      switchMap(filterProps => {
        dynamicSearchQuery = this.setDynamicSearchFilter(dynamicSearchQuery, this._association, filterProps);

        return this._ispmHttpClient.post('entity-tree/' + this._resource + '/dynamicSearch?type=statistics', dynamicSearchQuery)
          .pipe(
            map((data: any) => {
              return  { totalEntities: data.resource.query_statistics[this._resource]};
            })
          )
          .catch(err => {
            return Observable.throw(err);
          });
      })
    );
  }

  public getAllCounts(allFilterArray: any, dySearchConfig?: DynamicSearchPayload, fromQuickFilter: boolean = false) {
    let dynamicSearchQuery: DynamicSearchPayload;

    if (dySearchConfig) {
      dynamicSearchQuery = cloneDeep(dySearchConfig);
    } else {
      dynamicSearchQuery = cloneDeep(this._dynamicSearch);
    }

    dynamicSearchQuery.target._count = 0;
    dynamicSearchQuery.target._page  = 0;
    dynamicSearchQuery.sort  = [];
    dynamicSearchQuery = this.setAdditionalFilters(dynamicSearchQuery);
    dynamicSearchQuery = this.setDynamicSearchFilter(dynamicSearchQuery, this._association, allFilterArray, fromQuickFilter);

    return this._ispmHttpClient.post('entity-tree/' + this._resource + '/dynamicSearch?type=statistics', dynamicSearchQuery)
      .pipe(
        map((data: any) => {
          return { totalEntities: data.resource.query_statistics[this._resource]};
        })
      )
      .catch(err => {
        return Observable.throw(err);
      });
  }

  public getBundleDySearch(dyBundle: Array<BundleDynamicSearchPayload>) {
    dyBundle.forEach((bundle) => {
      bundle.searchBody = this.setAdditionalFilters(bundle.searchBody);
    });

    return this._ispmHttpClient.post('entity-tree/dynamicSearchBundle', dyBundle)
      .pipe(
      map((data: any) => {
        let result: any = {};
        result.additionalFilters = [];
        dyBundle.forEach((dySearch, index) => {
          //Building an object with two items:
          //1. 'generalFilters' which contains the counts of all general filters (user filters and predefined non-status filters are not inside). All counters are calcualted by one query.
          //2. 'additionalFilters' - separate count queries
          if (dyBundle.length > index + 1){
            result.additionalFilters.push({ totalEntities: data[index].resource.query_statistics[dySearch.resource]});
          }
          else{
            result.generalFilters = data[index].resource.query_statistics;
          }

        });
        return result;
      })
    ).catch(err => {
      return Observable.throw(err);
    });
  }

  public getResource(field_list, resources_ids, associationItems?: Array<any>, adaptableFilters?: any, additionalParams?: any) {
    return this.applyFilterProperties()
      .pipe(
        take(1),
        switchMap(filterProps => {
          let dySearch = this.setDynamicSearchFilterIds(resources_ids);
          const filter = this.createFilter(associationItems, filterProps);

          if (!!filter) {
            dySearch['filters'].push(filter);
          }

          dySearch = this.setAdaptableFilters(dySearch, adaptableFilters, filterProps);

          if (additionalParams && additionalParams['currentUserId']) {
            return zip(this._ispmHttpClient.post('entity-tree/' + this._resource + '/dynamicSearch?type=data', dySearch))
              .pipe(
                map(data => {
                  const dynamicSearchResDict = mapValues(keyBy(data[0], 'id'));
                  return { items: dynamicSearchResDict };
                })
              );
          } else {
            return this._ispmHttpClient.post('entity-tree/' + this._resource + '/dynamicSearch?type=data', dySearch)
              .pipe(
                map(data => {
                  return { items: mapValues(keyBy(data, 'id')) };
                })
              );
          }
        }
      ));
  }

  public getResourceIds(
    query: any,
    page: number,
    sortField: string,
    sortOrder: string | number,
    hasSearchMode,
    isClubbed: boolean = false,
    configTemplateType: string = '',
    resourceType: string = '',
    resourceIds?: string,
    sortByType?: string,
    sortPath = true,
    associationItems?: Array<any>,
    adaptableFilters?: any,
  ) {
    sortOrder = (sortOrder === -1 || sortOrder.toString().toLowerCase() === 'desc') ? 'DESC' : 'ASC';
    /* Dynamic search needs type=string instead of type=text */
    sortByType = (sortByType === 'text') ? 'string' : sortByType;

    let dynamicSearchQuery: DynamicSearchPayload = cloneDeep(this._dynamicSearch);
    let patientFilter: Filter;

    if (resourceIds) {
      const patientAssociation = associationItems ? find(associationItems, function(associationItem) { return associationItem.name === 'Patient'; }) : null;

      if (!!patientAssociation) {
        patientFilter = new Filter(patientAssociation.resource, patientAssociation.operator, resourceIds, patientAssociation.path, patientAssociation.type);
      }
    }

    return this.applyFilterProperties().pipe(
      take(1),
      switchMap(filterProps => {
        dynamicSearchQuery = this.populateFilterAndSort(dynamicSearchQuery, associationItems, filterProps, patientFilter, adaptableFilters, page, sortField, sortOrder, sortPath, sortByType);

        if (isClubbed) {
          return this._ispmHttpClient.post('entity-tree/' + this._resource + '/dynamicSearch?type=clubbed', dynamicSearchQuery)
            .pipe(
              map((data: any) => {
                // revCheckPoints is needed for Advanced\Revise action and it is calculated only for worklists that may have this action
                if (this._type.includes(WorklistHelper.PATIENT_DASHBOARD_TEST) || this._type === WorklistHelper.TEST_WORKLIST_TYPE) {
                  this.setCheckPointsData(data.data);
                }

                data.data = this.setIsTestOverdue(data.data);

                return {
                  resources_ids: data.ids,
                  resources_data: data.data,
                  totalEntities: data.stats.resource.query_statistics[this._resource]
                };
              })
            )
            .catch((err) => {
              return Observable.throw(err);
            });
        }

        return this._ispmHttpClient.post('entity-tree/' + this._resource + '/dynamicSearch?type=id', dynamicSearchQuery).pipe(
          map((data: any[]) => {
            return { resources_ids: data};
          })
        ).catch(err => {
          return Observable.throw(err);
        });
      })
    );
  }

  public getQuery(
    page: number,
    sortField: string,
    sortOrder: string | number,
    sortByType: string,
    sortPath = true,
    associationItems?: Array<any>,
    adaptableFilters?: any,
    resourceIds?: string
  ) {
    sortOrder = (sortOrder === -1 || sortOrder.toString().toLowerCase() === 'desc') ? 'DESC' : 'ASC';
    /* Dynamic search needs type=string instead of type=text */
    sortByType = (sortByType === 'text') ? 'string' : sortByType;

    let dynamicSearchQuery: DynamicSearchPayload = cloneDeep(this._dynamicSearch);
    let patientFilter: Filter;

    if (resourceIds) {
      const patientAssociation = associationItems ? find(associationItems, function(associationItem) { return associationItem.name === 'Patient'; }) : null;

      if (!!patientAssociation) {
        patientFilter =  new Filter(patientAssociation.resource, patientAssociation.operator, resourceIds, patientAssociation.path, patientAssociation.type);
      }
    }

    return this.applyFilterProperties()
      .pipe(
        take(1),
        switchMap(filterProps => {
          dynamicSearchQuery = this.populateFilterAndSort(dynamicSearchQuery, associationItems, filterProps, patientFilter, adaptableFilters, page, sortField, sortOrder, sortPath, sortByType);

          return of({
            resource: this._resource,
            type: 'dynamic',
            params: dynamicSearchQuery
          });
        })
      );
  }

  private populateFilterAndSort<A>(dynamicSearchQuery: DynamicSearchPayload, associationItems: Array<any>, filterProps: A, patientFilter: Filter, adaptableFilters: any, page: number, sortField: string, sortOrder: string | number, sortPath: boolean, sortByType: string) {
    dynamicSearchQuery = this.setDynamicSearchFilter(dynamicSearchQuery, associationItems, filterProps);

    if (patientFilter) {
      dynamicSearchQuery.filters.push(patientFilter);
    }

    dynamicSearchQuery = this.setAdditionalFilters(dynamicSearchQuery);
    dynamicSearchQuery.target._count = this._chunkAmount;
    // target can be re-written by adaptable filters
    dynamicSearchQuery = this.setAdaptableFilters(dynamicSearchQuery, adaptableFilters, filterProps);
    dynamicSearchQuery.target._page = (page == 0)? 0 : page -1 ;

    const associationItem = associationItems ? find(associationItems, (associationItem) => associationItem.tableKey === sortField) : null;

    if (associationItem) {
      let sortType: string = associationItem.dyType;
      if (!sortType){
        sortType = associationItem.sortTypeInFilterQuery ? associationItem.sortTypeInFilterQuery : associationItem.type;
      }
      dynamicSearchQuery.sort = [new Sort(associationItem.resource, associationItem.path, sortOrder.toString(), sortType)];
    } else {
      let resource;

      if (!dynamicSearchQuery.useSort) {
        const pathArray: any[] = sortField.split('.');

        if (pathArray.length > 1) {
          /* Sort on Child resource element - Searchable param must be available in database for this */
          resource = pathArray[0];
          sortField = pathArray[1].split('_')[0];
          dynamicSearchQuery.sort = [new Sort(resource, sortField, sortOrder.toString())];
        } else {
          dynamicSearchQuery.sort = [new Sort(this._resource, sortPath ? `{${sortField}}` : `${sortField}`, sortOrder.toString(), sortByType)];
        }
      } else {
        delete dynamicSearchQuery['useSort'];
      }
    }

    return dynamicSearchQuery;
  }

  public sendPureRequest(dynamicSearch: DynamicSearchPayload, resource, type) {
    return this._ispmHttpClient.post(`entity-tree/${resource}/dynamicSearch?type=${type}`, dynamicSearch)
      .catch(err => {
        return Observable.throw(err);
      });
  }

  private setIsTestOverdue(data: any[]): any[] {
    const dayInMilliseconds = (24 * 1000 * 3600);
    let maxAllowedTestDurationUntilOverdue;
    this._store.select((state) => state.users.organizationConfiguration.testElapsedTime).pipe(take(1)).subscribe((data => {
      maxAllowedTestDurationUntilOverdue = data;
    }));
    let revDatetime: Date;
    let isTestOverdue;

    if (maxAllowedTestDurationUntilOverdue) {
      lodash_each(data, item => {
          revDatetime = new Date(item.revDatetime);
          const diff = Math.ceil(Math.abs(new Date().getTime() - revDatetime.getTime()) / dayInMilliseconds);
          isTestOverdue = (diff >= maxAllowedTestDurationUntilOverdue);

          if (isTestOverdue) {
            item['isTestOverdue'] = isTestOverdue;
          }
      }
    )};

    return data;
  }

  private async setCheckPointsData(data) {
    let workflows = data || [];
    const promises = workflows.map(async item => {
      const revisionLike = {
          id: item.lastRevision,
          items: item.revItems,
          checkpoints: item.revCheckpoints
      } as Partial<IRevision>;
      item.revCheckpoints = ReviseService.filterAvailableCheckpoints([revisionLike] as any[], false);
      item.allowRevise = item.isLatestRevision && item.revCheckpoints.length > 0;

      const workflow = {
        id: item.workflowrequest_id,
        status: item.revStatus,
        revisions: [revisionLike],
        activeRevision: item.activeRevision
      };
      const definitions = await this.checkTestConfiguration(item.workflowRequestTemplateId); // "EdenPathwayTestV2-2b2c5cca-f5d1-11e5-bef2-000c29c80ba6");//item.workflowRequestTemplateId

      if (definitions) {
        item.readOnlyParameters = definitions.readOnlyMode;
        const templateRevise = ReviseService.getAvailableTemplates(definitions, workflow as any, revisionLike as any);
        item.revCheckpoints = [...item.revCheckpoints, ...templateRevise];
        item.allowRevise = item.isLatestRevision && item.revCheckpoints.length > 0;
        // this._changeDetector.markForCheck();
      }

      return item;
    });

    workflows = await Promise.all(promises);

    return workflows;
  }

  private checkTestConfiguration(testTypeId: string) {
    return this._configurationService.registerForConfiguration(testTypeId, 'test').toPromise();
  }

  public setDynamicSearchFilterIds(resources_ids: string[]) {
    const filtersToCall = [...this._dynamicSearch.filters];
    filtersToCall.push(new Filter(this._resource, '=',  resources_ids.join(','), '{id}', 'string'));
    const target: Target = Object.assign(this._dynamicSearch.target, { _count: resources_ids.length});

    return Object.assign({ }, this._dynamicSearch, { filters: filtersToCall, target});
  }

  public setDynamicSearchFilter(dynamicSearchQuery: DynamicSearchPayload, associationItems: Array<any>, filterProperties: any, fromQuickFilter: boolean = false): DynamicSearchPayload {
    const filter_Backup = [...dynamicSearchQuery.filters];

    if (!isEmpty(filterProperties)) {
      dynamicSearchQuery['filters'] =  filterProperties.map(item => {
        const associationItem = associationItems
          ? find(associationItems, function(associationItem) { return associationItem.name === item.name; })
          : null
        ;

        // If the filter item in filter form has 'overrideWorklistConfigInFilterQuery = true', do not take the query parameters from associationItem.
        // This handles the case that a certain filter is defined in associationItem with 'type = simplestring' which is good for the worklist
        // but in filters it should be defind as "string" to allow partial test search.
        if (associationItem) { // } && !(item.attributes && item.attributes.overrideWorklistConfigInFilterQuery)){
          if (item.attributes) {
            if (item.attributes.nameInFilterQuery) {
              associationItem.path = item.attributes.nameInFilterQuery;
            }

            if (item.attributes.typeInFilterQuery) {
              if (!associationItem.sortTypeInFilterQuery){ //sort type should not be changed even if filter type is changed so keeping the original sort type
                associationItem.sortTypeInFilterQuery = associationItem.type;
              }
              associationItem.type = item.attributes.typeInFilterQuery; //Should be use as a type for the filter BUT not for sort!!
            }

            if (associationItem.type === 'date' || associationItem.type === 'dateTime') {
              associationItem.rangeOperator = ['>=', '<='];
            }
          }
          return this.createFilterQueryFromAssociationItems(associationItem, item);
        } else {
          const itemName: string = item.attributes && item.attributes.nameInFilterQuery ? item.attributes.nameInFilterQuery : item.name;
          const itemType: string = item.attributes && item.attributes.typeInFilterQuery ? item.attributes.typeInFilterQuery : 'string';
          let rangeOperator: Array<string>;

          if (item.attributes && item.attributes.typeInFilterQuery === 'date') {
            rangeOperator = ['>=', '<='];
          }

          const associationItem = { resource: this._resource, operator: '=', path: itemName, type: itemType, rangeOperator};

          return this.createFilterQueryFromAssociationItems(associationItem, item);
        }
      });
      dynamicSearchQuery['filters'] = flatten(dynamicSearchQuery['filters']);
    }

    dynamicSearchQuery['filters'] = dynamicSearchQuery['filters'].concat(filter_Backup);
    dynamicSearchQuery['filters'] =  lodashFilter(dynamicSearchQuery['filters'], function (e) {
      return e != null;
    });
    dynamicSearchQuery['filters'] =  uniqWith(dynamicSearchQuery['filters'], isEqual);

    return dynamicSearchQuery;
  }

  public applyFilterProperties(): Observable<any[] | null> {
    return this._filterService.getAppliedFilterPropertiesFromStore();
   }

  public createFilterQueryFromAssociationItems(associationItem, item) {
    if (item && item.selectedValue && item.selectedValue.value) {
      const itemAttributes = get(item, 'attributes', { });

      if (associationItem.type === 'date' || associationItem.type === 'dateTime') {
        if (item.selectedValue.value && item.selectedValue.value.length > 1) {
          let i = 0;

          return item.selectedValue.value.map((selected) => {
            const operator = (associationItem.rangeOperator && associationItem.rangeOperator.length > i)
              ? associationItem.rangeOperator[i]
              : '=';

            i++;

            return new Filter(associationItem.resource, operator, this.convertDate(selected.id), associationItem.path, associationItem.dyType ? associationItem.dyType : associationItem.type);
          });
        } else {
          return new Filter(associationItem.resource, associationItem.operator, item.selectedValue.value.map(selected => this.convertDate(selected.id)).join(','), associationItem.path, associationItem.dyType ? associationItem.dyType : associationItem.type);
        }
      } else if (itemAttributes.hasOwnProperty('valueToCheck') && itemAttributes.hasOwnProperty('valueToSet') && itemAttributes.hasOwnProperty('operatorInFilterQuery')) {
        const andFilters = [];
        const includeValues = item.selectedValue.value.filter(oneValue => !get(itemAttributes, 'valueToCheck', []).includes(oneValue.id));
        const excludeValues = item.selectedValue.value.filter(oneValue => get(itemAttributes, 'valueToCheck', []).includes(oneValue.id));

        if (excludeValues && excludeValues.length > 0) {
          andFilters.push(new Filter(associationItem.resource, get(itemAttributes, 'operatorInFilterQuery', associationItem.operator), get(itemAttributes, 'valueToSet', []).join(','), associationItem.path, associationItem.dyType ? associationItem.dyType : associationItem.type));
        }

        if (includeValues && includeValues.length > 0) {
          andFilters.push(new Filter(associationItem.resource, associationItem.operator, includeValues.map(selected => selected.id).join(','), associationItem.path, associationItem.dyType ? associationItem.dyType : associationItem.type));
        }

        return andFilters;
      } else {
        if (associationItem.filterCondition) {
          const andFilters = [];

          item.selectedValue.value.forEach((oneValue) => {
            andFilters.push(new Filter(associationItem.resource, associationItem.operator, oneValue.id, associationItem.path, associationItem.dyType ? associationItem.dyType : associationItem.type));
          });

          return andFilters;
        } else {
          return new Filter(associationItem.resource, associationItem.operator, item.selectedValue.value.map(selected => selected.id).join(','), associationItem.path, associationItem.dyType ? associationItem.dyType : associationItem.type);
        }
      }
    }
  }

  private convertDate(date) {
    const momentValue = moment.utc(date);
    return momentValue.format('YYYY-MM-DD');
  }

  private setAdditionalFilters(dynamicSearchQuery: DynamicSearchPayload): any {
    if (this._type.includes(WorklistHelper.PATIENT_DASHBOARD_TEST)) {
      const patientId = this.routerManagerService.parameters.patientId;
      if (patientId) {
        dynamicSearchQuery.filters.push({
          'resource': 'UIPractWorkListRow',
          'operator': '=',
          'value': patientId,
          'path': '{patient_id}',
          'type': 'simplestring'
        });
      }
    }

    if (this._dashboardFilters) {
      if (dynamicSearchQuery.filters && dynamicSearchQuery.filters.length === 0) {
        dynamicSearchQuery.filters = this._dashboardFilters;
      } else {
        dynamicSearchQuery.filters = dynamicSearchQuery.filters.concat(this._dashboardFilters);
      }
    }

    return dynamicSearchQuery;
  }

  public setDashboardLevelFilters(dashboardFilters: Array<Filter>, reset: boolean = false) {
    if (reset) {
      this._dashboardFilters = [];
    } else {
      this._dashboardFilters = dashboardFilters;
    }
  }

  public interpolateDashboardLevelFilters(dashboardFilters: Array<Filter>, interpolateData: any = { }): Observable<Array<Filter>> {
    const values = [];
    // Two for each loops to avoid async on for loop to store interpolate to avoid performance issue
    if (!isEmpty(interpolateData)) {
      dashboardFilters.forEach((filter) => {
        values.push(this._aggregationService.interpolate(filter.value, interpolateData));
      });
    } else {
      dashboardFilters.forEach((filter) => {
        values.push(filter.value);
      });
    }

    return this._aggregationService.interPolateWithStoreQueries(values).map((interploatedValues) => {
      dashboardFilters.forEach((filter, index) => {
        filter.value = interploatedValues[index];
      });

      return dashboardFilters;
    });
  }

  private createFilter(associationItems: Array<any>, filterProperties: any[]) {
    if (!!associationItems && !!filterProperties &&  Array.isArray(filterProperties) && filterProperties.length > 0) {
      const associationItem = associationItems.find(associationItem => associationItem.name === filterProperties[0].name);

      if (!!associationItem) {
        return this.createFilterQueryFromAssociationItems(associationItem, filterProperties[0]);
      }
    }

    return undefined;
  }

  private setAdaptableFilters(dynamicSearchQuery: any, adaptableFilters: any, currentFilter: any) {
    const newFilters = this._adaptiveFilterService.getFilterByCategoryAndData(adaptableFilters, 'filterPanel', currentFilter);

    if (!!newFilters && newFilters.length > 0) {
      dynamicSearchQuery.filters = dynamicSearchQuery.filters.concat(newFilters);
    }

    const newRelations = this._adaptiveFilterService.getDescriptionByCategoryAndData('relations', adaptableFilters, 'filterPanel', currentFilter);

    if (!!newRelations && newRelations.length > 0) {
      dynamicSearchQuery.relations = dynamicSearchQuery.relations.concat(newRelations);
    }

    const newSorts = this._adaptiveFilterService.getDescriptionByCategoryAndData('sort', adaptableFilters, 'filterPanel', currentFilter);

    if (!!newSorts && newSorts.length > 0) {
      dynamicSearchQuery.sort = dynamicSearchQuery.sort.concat(newSorts);
    }

    return dynamicSearchQuery;
  }

  public getSelectedTreatment(revision: string) {
    return of({ });
  }

  public getResourceIdsPatientTests(patientId: string, orgId: string) {
    return of({ });
  }

  public getTreatments(patientId: string, revision: string) {
    return of({ });
  }
}
